import { Button, Stack, TextField, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useCreatePlatformReportMutation } from '../../api/platformReports'
import {
  PLATFORM_REPORT_REASON_LABELS,
  type PlatformReportReasonCode,
  type PlatformReportTargetType,
} from '../../api/types/platformReports'
import { extractApiErrorMessage } from '../../utils/api'
import modalEmitter from '../ModalComponent/modalEmitter'
import { AutocompleteInput } from '../ui-kit/AutocompleteInput'

const MESSAGE_MAX = 1000

const REASON_OPTIONS = Object.entries(PLATFORM_REPORT_REASON_LABELS) as Array<
  [PlatformReportReasonCode, string]
>

type Props = {
  targetType: PlatformReportTargetType
  targetId: number
  title?: string
}

export function ReportModalBody({ targetType, targetId, title }: Props) {
  const [reasonCode, setReasonCode] = useState<PlatformReportReasonCode | ''>('')
  const [message, setMessage] = useState('')
  const [createReport, { isLoading }] = useCreatePlatformReportMutation()
  const reasonOptions = useMemo(
    () => REASON_OPTIONS.map(([value, label]) => ({ value, label })),
    [],
  )

  const handleSubmit = async () => {
    if (!reasonCode) {
      toast.info('Выберите причину')
      return
    }
    try {
      await createReport({
        target_type: targetType,
        target_id: targetId,
        reason_code: reasonCode,
        ...(message.trim() ? { message: message.trim().slice(0, MESSAGE_MAX) } : {}),
      }).unwrap()
      toast.success('Жалоба отправлена на проверку')
      modalEmitter.emitCloseModal()
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось отправить жалобу')
    }
  }

  return (
    <Stack gap={2}>
      <Typography variant="black-14-light" color="text.secondary">
        {title ??
          'Жалоба уйдёт на проверку. Контент не удаляется автоматически.'}
      </Typography>
      <Stack gap={0.5}>
        <Typography variant="black-14-medium">Причина</Typography>
        <AutocompleteInput
          options={reasonOptions}
          value={reasonCode}
          onChange={(value) => setReasonCode((value as PlatformReportReasonCode) ?? '')}
          placeholder="Выберите..."
          fullWidth
          disabled={isLoading}
          disableClearable
        />
      </Stack>
      <Stack gap={0.5}>
        <Typography variant="black-14-medium">Комментарий (необязательно)</Typography>
        <TextField
          placeholder="Опишите проблему"
          multiline
          minRows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
          helperText={`${message.length}/${MESSAGE_MAX}`}
          fullWidth
          disabled={isLoading}
        />
      </Stack>
      <Stack direction="row" gap={1} justifyContent="flex-end">
        <Button variant="follow" disabled={isLoading} onClick={() => modalEmitter.emitCloseModal()}>
          Отмена
        </Button>
        <Button variant="danger" disabled={isLoading} onClick={() => void handleSubmit()}>
          Отправить жалобу
        </Button>
      </Stack>
    </Stack>
  )
}

export function openPlatformReportModal(args: {
  targetType: PlatformReportTargetType
  targetId: number
  dialogTitle?: string
}) {
  modalEmitter.emitOpenModal({
    dialog: {
      title: args.dialogTitle ?? 'Пожаловаться',
      body: (
        <ReportModalBody
          targetType={args.targetType}
          targetId={args.targetId}
        />
      ),
      modalToCentrPosition: true,
    },
    closeAtion: { visible: false },
    confirmAtion: { visible: false },
  })
}
